import React, { useState, useRef } from 'react';
import {
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import { Button } from 'components/ui';
import { jsPDF } from 'jspdf';
import html2canvasPro from 'html2canvas-pro';
import { FeeReceiptTemplate } from './FeeReceiptTemplate';
import { formatAmount } from 'utils/paymentFormatters.jsx';

export const PaymentLinkSuccess = ({
  createdLinkData,
  copySuccess,
  onCopy,
  onClose,
  selectedGateway,
  showFooterButton = true,
  isFeePayment,
  leadData,
  feeBreakup,
  paymentFormData = {},
}) => {
  const [isGeneratingReceipt, setIsGeneratingReceipt] = useState(false);
  const receiptRef = useRef(null);
  const isManualPayment = selectedGateway?.GatewayCode === 'MAN';

  // Use createdLinkData amount if available, handle array response from API
  const linkData = Array.isArray(createdLinkData) ? createdLinkData[0] : createdLinkData;
  
  // Prioritize the actual recorded payment amount from the response
  const recordedAmount = linkData?.Payments?.[0]?.PaymentAmount || linkData?.Amount || linkData?.amount || linkData?.PaidAmount;
  
  const displayAmount = (recordedAmount !== undefined && recordedAmount !== null && recordedAmount !== '')
    ? parseFloat(recordedAmount)
    : parseFloat(paymentFormData?.amount || 0);

  const displayCurrency = linkData?.Payments?.[0]?.PaymentCurrency || linkData?.Currency || linkData?.currency || paymentFormData?.currency || 'INR';

  const handleDownloadReceipt = async () => {
    if (!receiptRef.current) return;
    
    setIsGeneratingReceipt(true);
    try {
      const element = receiptRef.current;
      const canvas = await html2canvasPro(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [canvas.width / 2, canvas.height / 2],
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 2, canvas.height / 2);
      pdf.save(`Fee_Receipt_${leadData?.name || 'Student'}.pdf`);
    } catch (error) {
      console.error('Receipt generation failed:', error);
    } finally {
      setIsGeneratingReceipt(false);
    }
  };

  // Map student info and payment details for the receipt
  const studentInfo = {
    name: leadData?.name,
    ERPNumber: leadData?.id ? String(leadData.id).padStart(6, '0') : 'N/A',
    course: feeBreakup?.academic?.courseName,
    batch: '2024-2025' // Default batch
  };

  // Calculate discount info for the receipt
  const academicAmount = parseFloat(feeBreakup?.academic?.amount || 0);
  const hostelAmount = parseFloat(feeBreakup?.hostel?.amount || 0);
  const ptuFee = parseFloat(feeBreakup?.academic?.ptuFee || 0);
  const kitFee = parseFloat(feeBreakup?.academic?.kitFee || 0);
  const totalBaseFee = academicAmount + hostelAmount + ptuFee + kitFee;

  const discountValue = parseFloat(paymentFormData?.DiscountValue || 0);
  let discountAmount = 0;
  if (paymentFormData?.IsDiscountApplied) {
    if (paymentFormData?.IsDiscountTypePercentage) {
      discountAmount = (academicAmount * discountValue) / 100;
    } else {
      discountAmount = discountValue;
    }
  }

  const paymentDetails = {
    receiptNumber: linkData?.ReceiptNumber || linkData?.PaymentId || 'N/A',
    paymentDate: new Date().toISOString(),
    paymentMode: linkData?.PaymentMethod || 'Manual',
    referenceNumber: linkData?.TransactionId,
    amountPaid: displayAmount,
    feeDescription: linkData?.Description || 'Fee Payment',
    originalFee: totalBaseFee,
    discountAmount: discountAmount,
    discountValue: discountValue,
    isDiscountPercentage: paymentFormData?.IsDiscountTypePercentage,
    isDiscountApplied: paymentFormData?.IsDiscountApplied && discountAmount > 0,
    netPayable: totalBaseFee - discountAmount,
  };

  return (
    <div className="space-y-4">
      {/* Hidden layout for PDF generation */}
      <div className="fixed -left-[9999px] -top-[9999px]">
        <FeeReceiptTemplate 
          ref={receiptRef} 
          studentInfo={studentInfo} 
          paymentDetails={paymentDetails}
          collegeName={feeBreakup?.academic?.collegeName}
        />
      </div>

      {/* Success Icon and Message */}
      <div className="flex flex-col items-center justify-center py-6">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
          <CheckCircleIcon className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {isManualPayment ? 'Manual Payment Recorded!' : 'Payment Link Created!'}
        </h3>
        <p className="text-center text-sm text-gray-600 dark:text-gray-400">
          {isManualPayment 
            ? 'The manual payment has been successfully recorded in the system'
            : 'Share this link with the customer to collect payment'
          }
        </p>

        {/* {isManualPayment && isFeePayment && (
          <button
            onClick={handleDownloadReceipt}
            disabled={isGeneratingReceipt}
            className="mt-4 flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-4 py-1.5 text-xs font-bold text-primary-700 hover:bg-primary-100 transition-all dark:bg-primary-900/20 dark:border-primary-800 dark:text-primary-300"
          >
            {isGeneratingReceipt ? (
              <span className="animate-pulse">Generating...</span>
            ) : (
              <>
                <DocumentArrowDownIcon className="h-4 w-4" />
                Download Receipt (PDF)
              </>
            )}
          </button>
        )} */}
      </div>

      {/* Payment Link Display - Only for non-manual payments */}
      {!isManualPayment && createdLinkData?.LinkUrl && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
          <p className="mb-2 text-xs font-medium text-gray-700 dark:text-gray-300">
            Payment Link
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={createdLinkData.LinkUrl}
              readOnly
              className="flex-1 rounded border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
            <Button
              variant="soft"
              color="primary"
              size="sm"
              onClick={onCopy}
              className="flex-shrink-0"
            >
              {copySuccess ? (
                <>
                  <ClipboardDocumentCheckIcon className="h-4 w-4" />
                  <span className="ml-1">Copied!</span>
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  <span className="ml-1">Copy</span>
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Manual Payment Details - Only for manual payments */}
      {isManualPayment && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
          <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-dark-500">
            Transaction Details
          </p>
          <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
            <div className="flex justify-between border-b border-gray-100 pb-1 dark:border-dark-700">
              <span className="font-medium">Gateway:</span> 
              <span className="font-bold text-gray-900 dark:text-dark-100">{selectedGateway?.GatewayName}</span>
            </div>
            <div className="flex justify-between border-b border-gray-100 pb-1 dark:border-dark-700">
              <span className="font-medium">Amount:</span>
              <span className="font-black text-emerald-600 dark:text-emerald-400">{formatAmount(displayAmount, displayCurrency)}</span>
            </div>
            {paymentFormData?.courseCode && (
              <div className="flex justify-between border-b border-gray-100 pb-1 dark:border-dark-700">
                <span className="font-medium">Course Code:</span> 
                <span className="font-bold text-red-600 dark:text-red-400 uppercase">{paymentFormData.courseCode}</span>
              </div>
            )}
            {createdLinkData?.TransactionId && (
              <div className="flex justify-between border-b border-gray-100 pb-1 dark:border-dark-700">
                <span className="font-medium">Ref No:</span> 
                <span className="font-bold text-gray-900 dark:text-dark-100 uppercase">{createdLinkData.TransactionId}</span>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Close Button */}
      {showFooterButton && (
        <Button 
          variant="solid" 
          color="success" 
          onClick={onClose} 
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white border-transparent"
        >
          Done
        </Button>
      )}
    </div>
  );
};

